import React from 'react';
import { IconLoader } from '@tabler/icons-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
    size?: 'sm' | 'md' | 'lg';
    loading?: boolean;
    icon?: React.ReactNode;
    children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    children,
    className = '',
    disabled,
    ...props
}) => {
    const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200';

    const variantClasses = {
        primary: 'text-white bg-primary hover:bg-red-700 focus:ring-primary border border-transparent',
        secondary: 'text-gray-700 bg-white hover:bg-gray-50 focus:ring-primary border border-gray-300',
        success: 'text-white bg-green-600 hover:bg-green-700 focus:ring-green-500 border border-transparent',
        warning: 'text-white bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500 border border-transparent',
        danger: 'text-white bg-red-600 hover:bg-red-700 focus:ring-red-500 border border-transparent',
    };

    const sizeClasses = {
        sm: 'px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm',
        md: 'px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm',
        lg: 'px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base',
    };

    const disabledClasses = 'opacity-50 cursor-not-allowed';

    const classes = `
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${(disabled || loading) ? disabledClasses : ''}
        ${className}
    `.trim().replace(/\s+/g, ' ');

    return (
        <button
            className={classes}
            disabled={disabled || loading}
            {...props}
        >
            {loading ? (
                <>
                    <IconLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Načítavam...
                </>
            ) : (
                <>
                    {icon && <span className="mr-2">{icon}</span>}
                    {children}
                </>
            )}
        </button>
    );
};

export default Button;
